import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Save, Loader2, Wallet, ReceiptText, Truck, Hand, BellRing, Store, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { ShopNav } from "../components/shop/ShopNav";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, errorMessage } from "../lib/api";
import { useLang } from "../context/LanguageContext";

function Section({ Icon, title, description, children, testId, right }) {
  return (
    <Card className="border-border" data-testid={testId}>
      <CardContent className="p-5 sm:p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand))] shrink-0"><Icon className="w-4 h-4" /></span>
            <div>
              <h2 className="font-heading font-semibold">{title}</h2>
              {description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
            </div>
          </div>
          {right}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

export default function ShopSettingsPage() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/my/shop")
      .then((res) => {
        setData(res.data);
        const s = res.data.settings;
        setForm({ ...s, shipping_price: String(s.shipping_price ?? 0), invoice_due_days: String(s.invoice_due_days ?? 14), vat_rate: String(s.vat_rate ?? 25) });
      })
      .catch((err) => toast.error(errorMessage(err)));
  }, []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, shipping_price: Math.max(0, parseInt(form.shipping_price, 10) || 0), invoice_due_days: Math.max(1, parseInt(form.invoice_due_days, 10) || 14), vat_rate: parseInt(form.vat_rate, 10) };
      delete payload.organizer_id;
      delete payload.updated_at;
      const res = await api.put("/my/shop/settings", payload);
      setData((d) => ({ ...d, settings: res.data.settings, vipps_available: res.data.vipps_available, invoice_available: res.data.invoice_available, can_sell: res.data.can_sell }));
      toast.success(t("Innstillingene er lagret"));
      if (res.data.warning) toast.warning(res.data.warning);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const accountDigits = (form?.invoice_account || "").replace(/\D/g, "");
  const accountOk = accountDigits.length === 11;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="shop-settings-page">
        <ShopNav openCount={data?.metrics?.to_fulfill || 0} />
        <h2 className="text-lg font-heading font-semibold tracking-tight">{t("Innstillinger for Butikk")}</h2>
        {!form ? (
          <div className="space-y-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
        ) : (
          <form onSubmit={save} className="space-y-5" data-testid="shop-settings-form">
            <Section Icon={Store} title={t("Åpen for publikum")} description={t("Når butikken er åpen, vises varene dine på rightin.no/butikk og på arrangementssidene dine.")} testId="settings-section-open"
              right={<Switch checked={!!form.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="settings-enabled-switch" />}>
              <div className="space-y-1.5">
                <Label htmlFor="s-intro">{t("Om butikken")}</Label>
                <Textarea id="s-intro" value={form.intro} onChange={(e) => set("intro", e.target.value)} rows={3} maxLength={1500} placeholder={t("Kort tekst øverst i butikken din, f.eks. hva dere selger og hva inntektene går til.")} data-testid="settings-intro-textarea" />
              </div>
            </Section>

            <div className="grid gap-5 md:grid-cols-2">
              <Section Icon={Hand} title={t("Henting hos dere")} description={t("Kunden henter varene hos dere etter avtale.")} testId="settings-section-pickup"
                right={<Switch checked={!!form.pickup_enabled} onCheckedChange={(v) => set("pickup_enabled", v)} data-testid="settings-pickup-switch" />}>
                <div className="space-y-1.5">
                  <Label htmlFor="s-pickup">{t("Hentested")}</Label>
                  <Input id="s-pickup" value={form.pickup_place} onChange={(e) => set("pickup_place", e.target.value)} maxLength={200} placeholder={t("F.eks. «Kulturhuset, resepsjonen, man–fre 10–15»")} disabled={!form.pickup_enabled} data-testid="settings-pickup-place-input" />
                </div>
              </Section>
              <Section Icon={Truck} title={t("Sending i posten")} description={t("Kunden oppgir adresse, og fraktprisen legges til bestillingen.")} testId="settings-section-shipping"
                right={<Switch checked={!!form.shipping_enabled} onCheckedChange={(v) => set("shipping_enabled", v)} data-testid="settings-shipping-switch" />}>
                <div className="space-y-1.5">
                  <Label htmlFor="s-ship">{t("Fast fraktpris (kr)")}</Label>
                  <Input id="s-ship" type="number" min={0} max={5000} inputMode="numeric" value={form.shipping_price} onChange={(e) => set("shipping_price", e.target.value)} disabled={!form.shipping_enabled} className="w-40" data-testid="settings-shipping-price-input" />
                </div>
              </Section>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <Section Icon={ReceiptText} title={t("Faktura som betalingsvalg")} description={t("Right in lager fakturaen (PDF) og sender den til kjøperen. Du markerer bestillingen som betalt når pengene er på konto.")} testId="settings-section-invoice"
                right={<Switch checked={!!form.invoice_enabled} onCheckedChange={(v) => set("invoice_enabled", v)} data-testid="settings-invoice-switch" />}>
                <div className="space-y-1.5">
                  <Label htmlFor="s-acc">{t("Kontonummer (11 siffer)")}</Label>
                  <div className="flex items-center gap-2">
                    <Input id="s-acc" value={form.invoice_account} onChange={(e) => set("invoice_account", e.target.value)} inputMode="numeric" maxLength={20} placeholder="1234 56 78903" className="font-mono" disabled={!form.invoice_enabled} data-testid="settings-invoice-account-input" />
                    {form.invoice_enabled && form.invoice_account && (accountOk ? <CheckCircle2 className="w-5 h-5 text-[hsl(var(--success))] shrink-0" /> : <AlertTriangle className="w-5 h-5 text-[hsl(var(--warning))] shrink-0" title={t("Kontonummeret må ha 11 siffer")} />)}
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="s-due">{t("Forfall (dager)")}</Label>
                    <Input id="s-due" type="number" min={1} max={90} inputMode="numeric" value={form.invoice_due_days} onChange={(e) => set("invoice_due_days", e.target.value)} disabled={!form.invoice_enabled} data-testid="settings-invoice-due-days-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("MVA-registrert")}</Label>
                    <div className="flex items-center gap-3 h-10">
                      <Switch checked={!!form.vat_registered} onCheckedChange={(v) => set("vat_registered", v)} data-testid="settings-vat-switch" />
                      {form.vat_registered && (
                        <Select value={form.vat_rate} onValueChange={(v) => set("vat_rate", v)}>
                          <SelectTrigger className="w-28" data-testid="settings-vat-rate-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{["0", "12", "15", "25"].map((v) => <SelectItem key={v} value={v}>{v} %</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{t("Når dette er på, viser fakturaen MVA-beløpet (prisene dine er inkl. MVA).")}</p>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="s-note">{t("Melding på fakturaen (valgfritt)")}</Label>
                  <Textarea id="s-note" value={form.invoice_note} onChange={(e) => set("invoice_note", e.target.value)} rows={2} maxLength={600} disabled={!form.invoice_enabled} data-testid="settings-invoice-note-textarea" />
                </div>
                <label className="flex items-start justify-between gap-3 rounded-lg border border-border p-3 text-sm cursor-pointer" data-testid="settings-invoice-reminders-row">
                  <span>
                    <span className="block font-medium">{t("Automatisk påminnelse ved forfalt faktura")}</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">{t("Kjøperen får én påminnelse på e-post 3 dager etter forfall (med fakturaen vedlagt). Du får kopi. Du kan også sende påminnelse manuelt fra ordrelisten.")}</span>
                  </span>
                  <Switch checked={form.invoice_reminders_enabled !== false} onCheckedChange={(v) => set("invoice_reminders_enabled", v)} disabled={!form.invoice_enabled} data-testid="settings-invoice-reminders-switch" />
                </label>
              </Section>

              <div className="space-y-5">
                <Section Icon={Wallet} title={t("Vipps som betalingsvalg")} description={t("Vipps bruker det samme oppsettet som billettene dine (Betaling i menyen). Pengene går rett til din Vipps-konto.")} testId="settings-section-vipps"
                  right={<Badge variant="outline" className={data?.vipps_available ? "text-[hsl(var(--success))] border-[hsl(var(--success))]/40" : "text-muted-foreground"} data-testid="settings-vipps-status">{data?.vipps_available ? t("Vipps er klart") : t("Vipps mangler")}</Badge>}>
                  {!data?.vipps_available && <Button asChild variant="outline" size="sm" data-testid="settings-vipps-link"><Link to="/dashboard/betaling">{t("Sett opp Vipps")}</Link></Button>}
                </Section>
                <Section Icon={BellRing} title={t("Varsler")} testId="settings-section-notify">
                  <label className="flex items-center justify-between gap-3 text-sm"><span>{t("E-post ved ny bestilling")}</span><Switch checked={!!form.notify_new_orders} onCheckedChange={(v) => set("notify_new_orders", v)} data-testid="settings-notify-orders-switch" /></label>
                  <label className="flex items-center justify-between gap-3 text-sm"><span>{t("E-post ved lavt lager")}</span><Switch checked={!!form.low_stock_notify} onCheckedChange={(v) => set("low_stock_notify", v)} data-testid="settings-notify-low-stock-switch" /></label>
                  <div className="space-y-1.5">
                    <Label htmlFor="s-notify">{t("Send varsler til (valgfritt)")}</Label>
                    <Input id="s-notify" type="email" value={form.notify_email} onChange={(e) => set("notify_email", e.target.value)} maxLength={200} placeholder={t("Tom = kontoens e-postadresse")} data-testid="settings-notify-email-input" />
                  </div>
                </Section>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" disabled={saving} data-testid="settings-save-button">{saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} {saving ? t("Lagrer …") : t("Lagre")}</Button>
              <span className="text-xs text-muted-foreground">{t("Right in tar ingen avgift på butikksalg.")}</span>
            </div>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
}
